/**
 * Theme Utilities
 *
 * Functions to convert theme objects to CSS custom properties
 * and apply them to the document.
 */

/**
 * Converts a camelCase string to kebab-case
 * e.g., "panelPrimary" -> "panel-primary"
 */
const toKebabCase = (str) => {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

/**
 * Flattens a nested theme object into CSS variable name/value pairs
 *
 * @param {Object} obj - Theme object (can be nested)
 * @param {string} prefix - CSS variable prefix (default: '--color')
 * @returns {Object} - Flat object with CSS variable names as keys
 */
const flattenTheme = (obj, prefix = '--color') => {
  const result = {};

  const flatten = (current, currentPrefix) => {
    for (const [key, value] of Object.entries(current)) {
      const cssVarName = `${currentPrefix}-${toKebabCase(key)}`;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        flatten(value, cssVarName);
      } else {
        result[cssVarName] = value;
      }
    }
  };

  flatten(obj, prefix);
  return result;
};

/**
 * Generates CSS variable declarations from a theme object
 *
 * @param {Object} theme - Theme object with colors and shadows
 * @returns {Object} - Object with CSS variable names as keys and values
 */
export const generateCSSVariables = (theme) => {
  if (!theme || !theme.colors) {
    throw new Error('Theme must have a colors object');
  }

  const variables = {};

  // Process colors
  for (const [key, value] of Object.entries(theme.colors)) {
    variables[`--color-${toKebabCase(key)}`] = value;
  }

  // Process shadows
  if (theme.shadows) {
    for (const [key, value] of Object.entries(theme.shadows)) {
      variables[`--shadow-${toKebabCase(key)}`] = value;
    }
  }

  return variables;
};

/**
 * Applies CSS variables to the document root element
 *
 * @param {Object} variables - Object with CSS variable names as keys
 */
export const applyCSSVariables = (variables) => {
  const root = document.documentElement;

  for (const [name, value] of Object.entries(variables)) {
    root.style.setProperty(name, value);
  }
};

/**
 * Removes CSS variables from the document root element
 *
 * @param {Object} variables - Object with CSS variable names as keys
 */
export const removeCSSVariables = (variables) => {
  const root = document.documentElement;

  for (const name of Object.keys(variables)) {
    root.style.removeProperty(name);
  }
};

/**
 * Applies a complete theme to the document
 *
 * @param {Object} theme - Theme object
 */
export const applyTheme = (theme) => {
  const variables = generateCSSVariables(theme);
  const colorMode = theme.mode || (theme.name.includes('light') ? 'light' : 'dark');

  applyCSSVariables(variables);
  document.documentElement.setAttribute('data-theme', theme.name);
  document.documentElement.setAttribute('data-color-mode', colorMode);
  document.documentElement.style.colorScheme = colorMode;
};

export default {
  generateCSSVariables,
  applyCSSVariables,
  removeCSSVariables,
  applyTheme,
};
